/**
 * Base implementation of the "Autocomplete" form field shared between "AutocompleteField" and
 * "AsyncAutocompleteField". This component is not supposed to be used out these.
 */

import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Close from '@mui/icons-material/Close';
import { inputBaseClasses } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';

import { SelectOption } from '~components/FormFields/Parts/SelectOption';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';

import Label from '../Label';
import { AutocompleteBaseProps } from './types';

const DEFAULT_LIMIT_TAGS = 3;

// The header reaches the paper through context so the paper slot stays one stable
// component; a component defined per render would remount the open dropdown.
const ListboxHeaderContext = createContext<React.ReactNode>(null);

const PaperWithListboxHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function PaperWithListboxHeader({ children, ...props }, ref) {
  const listboxHeader = useContext(ListboxHeaderContext);

  return (
    <Paper ref={ref} {...props}>
      {listboxHeader && (
        <Box
          // keeps focus in the input so a header click doesn't blur and close the dropdown
          onMouseDown={(event) => event.preventDefault()}
          // MUI leaves its highlight on the last hovered option, so without this the
          // header and that option would both look hovered
          onMouseEnter={(event) =>
            event.currentTarget.parentElement
              ?.querySelectorAll('[role="option"].Mui-focused')
              .forEach((option) =>
                option.classList.remove('Mui-focused', 'Mui-focusVisible'),
              )
          }
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {listboxHeader}
        </Box>
      )}
      {children}
    </Paper>
  );
});

export default function AutocompleteBase<T extends FieldValues, ParsedType>({
  ListboxProps,
  autoFocus,
  createEntityParams,
  disableClearable,
  error,
  filterOptions,
  groupBy,
  inputValue,
  isDisabled,
  isLoading,
  isMultiple,
  isOpen,
  isOptionInvalid,
  isRequired,
  label,
  limitTags = DEFAULT_LIMIT_TAGS,
  listboxHeader,
  name,
  onChange,
  onClose,
  onInputChange,
  onOpen,
  options,
  renderSecondary,
  sx,
  value,
}: AutocompleteBaseProps<T, ParsedType>) {
  const isCreatable = Boolean(createEntityParams);

  const [createEntityChip, setCreateEntityChip] = useState<
    Omit<CreateEntityChipProps, 'onClick' | 'target'>
  >({ createSuccess: false, isCreating: false, text: '' });

  const handleInputChange = useCallback<
    AutocompleteBaseProps<T, ParsedType>['onInputChange']
  >(
    (...args) => {
      if (isCreatable) {
        setCreateEntityChip((state) => ({ ...state, text: args[1] }));
      }

      onInputChange(...args);
    },
    [isCreatable, onInputChange],
  );

  const handleCreateEntityChipClick = useCallback(async () => {
    if (!createEntityParams?.onCreate) {
      return;
    }

    setCreateEntityChip((state) => ({
      ...state,
      createSuccess: false,
      isCreating: true,
    }));

    try {
      const newOption = await createEntityParams.onCreate(createEntityChip.text);

      if (newOption) {
        setCreateEntityChip((state) => ({
          ...state,
          isCreating: false,
          createSuccess: true,
        }));

        const timeout = setTimeout(() => {
          clearTimeout(timeout);
          onChange(newOption);
          onClose();
        }, 1000);
      }
    } catch {
      setCreateEntityChip((state) => ({
        ...state,
        isCreating: false,
        createSuccess: false,
      }));
    }
  }, [createEntityChip.text, createEntityParams?.onCreate, onClose, onChange]);

  useEffect(() => {
    if (!isOpen && isCreatable) {
      setCreateEntityChip((state) => ({
        ...state,
        createSuccess: false,
        isCreating: false,
      }));
    }
  }, [isCreatable, isOpen]);

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <ListboxHeaderContext.Provider value={listboxHeader}>
        <Autocomplete
          clearOnBlur
          disableClearable={isLoading ? true : disableClearable}
          disableCloseOnSelect={isMultiple}
          disablePortal={false}
          disabled={isDisabled}
          filterOptions={filterOptions}
          getOptionLabel={(item) => item?.label ?? ''}
          groupBy={groupBy}
          id={name}
          inputValue={inputValue}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          loading={isLoading}
          multiple={isMultiple}
          openOnFocus={autoFocus}
          noOptionsText={
            createEntityParams && inputValue.length ? (
              <CreateEntityChip
                createSuccess={createEntityChip.createSuccess}
                isCreating={createEntityChip.isCreating}
                onClick={handleCreateEntityChipClick}
                text={createEntityChip.text}
                target={createEntityParams.target}
              />
            ) : (
              t('form_fields.no_options')
            )
          }
          onChange={(_event, value) => onChange(value ?? null)}
          onClose={onClose}
          onInputChange={handleInputChange}
          onOpen={onOpen}
          open={isOpen}
          options={options}
          slotProps={{ listbox: ListboxProps }}
          slots={listboxHeader ? { paper: PaperWithListboxHeader } : undefined}
          value={value}
          renderInput={(params) => (
            <TextField
              {...params}
              // oxlint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              className={isRequired && !isDisabled ? 'required' : ''}
              error={Boolean(error)}
              sx={{ [`.${inputBaseClasses.root}`]: { flexWrap: 'nowrap' } }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && (
                        <Box
                          sx={{
                            height: '18px',
                            pointerEvents: 'none',
                            position: 'absolute',
                            right: '39px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '18px',
                          }}
                        >
                          <CircularProgress size={18} color="secondary" />
                        </Box>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option, { inputValue, selected }) => (
            <SelectOption
              key={option.value}
              getLabel={() => option.label}
              inputValue={inputValue}
              isCheckEnabled={Boolean(isMultiple)}
              option={option}
              props={props}
              secondary={renderSecondary?.(option, inputValue)}
              selected={selected}
            />
          )}
          renderValue={
            isMultiple
              ? (selectedOptions, getItemProps) =>
                  Array.isArray(selectedOptions) ? (
                    <>
                      {selectedOptions.slice(0, limitTags).map((option, index) => (
                        <Chip
                          size="small"
                          color={isOptionInvalid?.(option) ? 'error' : 'primary'}
                          label={option.label}
                          deleteIcon={<Close />}
                          sx={{ maxWidth: '70%' }}
                          {...getItemProps({ index })}
                          key={option.value}
                        />
                      ))}

                      {selectedOptions.length > limitTags && (
                        <Typography>+{selectedOptions.length - limitTags}</Typography>
                      )}
                    </>
                  ) : null
              : undefined
          }
        />
      </ListboxHeaderContext.Provider>

      {error && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}

interface CreateEntityChipProps {
  createSuccess: boolean;
  isCreating: boolean;
  onClick: () => void;
  target: string;
  text: string;
}

function CreateEntityChip({
  createSuccess,
  isCreating,
  onClick,
  target,
  text,
}: CreateEntityChipProps) {
  const icon = useMemo(() => {
    if (isCreating) {
      return (
        <Box
          sx={{
            alignContent: 'center',
            display: 'flex',
            height: '18px',
            justifyContent: 'center',
            pt: '2px',
            pl: '1px',
            width: '18px',
          }}
        >
          <CircularProgress size={14} color="secondary" />
        </Box>
      );
    }

    if (createSuccess) {
      return <CheckCircleOutline />;
    }

    return <AddCircleOutline />;
  }, [isCreating, createSuccess]);

  const label = useMemo(() => {
    if (isCreating) {
      return t('autocomplete.creating_entity', { entity: target });
    }

    if (createSuccess) {
      return t('autocomplete.entity_created', { entity: target });
    }

    return text;
  }, [isCreating, createSuccess, text]);

  return (
    <Tooltip
      title={
        <Typography sx={{ fontSize: '11px' }}>
          {t('autocomplete.create_entity', { entity: target })} <b>{text}</b>
        </Typography>
      }
    >
      <Chip
        color={createSuccess ? 'success' : 'primary'}
        disabled={isCreating}
        icon={icon}
        onClick={onClick}
        size="small"
        label={
          <OverflowAwareText
            sx={{ fontWeight: 600, ...(createSuccess ? { color: 'white' } : {}) }}
          >
            {label}
          </OverflowAwareText>
        }
        sx={{ cursor: isCreating ? 'not-allowed' : 'pointer' }}
      />
    </Tooltip>
  );
}
